import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith, finalize } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatSort } from '@angular/material/sort';
import { showSpinner, hideSpinner, getCurrentUser } from 'src/app/core/services/functions.service';
import { NotificationHelper } from 'src/app/core/services/helpers/notification.helper';
import { ComsTiendaCitaFutura } from '../../models/coms-tienda-cita-futura.model';
import { ComsTiendaCitaFuturaService } from '../../services/coms-tienda-cita-futura.service';
import { Tienda } from '../../models/tienda-cita-futura.model';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { PeriodosmetasService, PeriodoMetas, ApiResponse } from '../../../periodosmetas/services/periodosmetas.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-citafutura',
  templateUrl: './citafutura.component.html',
  styleUrls: ['./citafutura.component.scss']
})
export class CitafuturaComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('auto') autocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChild('departamentoAutocompleteTrigger') departamentoAutocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChild('autoTienda') tiendaAutocompleteTrigger!: MatAutocompleteTrigger;

  citaForm: FormGroup;
  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = ['periodo', 'codigo_agencia', 'nombre_agencia', 'acciones'];
  registrosCitaFutura: ComsTiendaCitaFutura[] = [];
  registroSeleccionado: ComsTiendaCitaFutura | null = null;
  isEditing = false;
  isLoading = false;

  // Form Controls
  ciudadCtrl = new FormControl('');
  departamentoCtrl = new FormControl('');
  tiendaCtrl = new FormControl('');

  // Filtered Options
  ciudadesFiltradas: Observable<{codigo: string, nombre_municipio: string}[]>;
  departamentosFiltrados: Observable<{codigo: string, nombre_departamento: string}[]>;
  tiendasFiltradas: Observable<Tienda[]>;

  // Selected Items
  ciudadesSeleccionadas: {codigo: string, nombre_municipio: string}[] = [];
  departamentosSeleccionados: {codigo: string, nombre_departamento: string}[] = [];
  tiendasSeleccionadas: Tienda[] = [];

  // Available Items
  todosLosDepartamentos: {codigo: string, nombre_departamento: string}[] = [];
  todasLasCiudades: {codigo: string, nombre_municipio: string}[] = [];
  tiendasDisponibles: Tienda[] = [];
  todasLasTiendas: Tienda[] = [];
  periodosAbiertos: PeriodoMetas[] = [];
  todosLosPeriodos: PeriodoMetas[] = [];
  periodoFiltro: string = '';
  sourceIsEmpty = false;

  pageSizeOptions: number[] = [5, 10, 25, 100];
  totalItems = 0;
  currentPage = 0;
  pageSize = 10;
  periodoSeleccionado: string = '';

  constructor(
    private fb: FormBuilder,
    private comsTiendaCitaFuturaService: ComsTiendaCitaFuturaService,
    private notificationService: NotificationHelper,
    private periodosService: PeriodosmetasService,
    private dialog: MatDialog
  ) {
    
    this.dataSource = new MatTableDataSource<any>([]);
    this.pageSizeOptions = [5, 10, 25, 100];
    this.pageSize = this.pageSizeOptions[1];
    
    this.citaForm = this.fb.group({
      periodo: ['', Validators.required],
      departamento: [''],
      ciudad: [''],
      tienda: ['']
    });

    this.tiendaCtrl = new FormControl();
    this.departamentoCtrl = new FormControl();
    this.ciudadCtrl = new FormControl();
    
    // Configurar el filtro personalizado para buscar por código y nombre de agencia
    this.dataSource.filterPredicate = (data: ComsTiendaCitaFutura, filter: string) => {
      if (!filter) return true;
      
      const searchStr = filter.toLowerCase();
      const codigoAgencia = (data.c_age || '').toLowerCase();
      const tienda = this.todasLasTiendas.find(t => t.c_age === data.c_age);
      const nombreAgencia = (tienda?.nom || '').toLowerCase();
      
      return codigoAgencia.includes(searchStr) || nombreAgencia.includes(searchStr);
    };
    
    // Initialize filtered observables
    this.ciudadesFiltradas = this.ciudadCtrl.valueChanges.pipe(
      startWith(null),
      map(value => this._filterCiudades(value))
    );

    this.departamentosFiltrados = this.departamentoCtrl.valueChanges.pipe(
      startWith(null),
      map(value => this._filterDepartamentos(value))
    );

    this.tiendasFiltradas = this.tiendaCtrl.valueChanges.pipe(
      startWith(null),
      map(value => this._filterTiendas(value))
    );
  }

  ngOnInit(): void {
    showSpinner('Cargando datos...');
    
    // Cargar todos los datos necesarios en paralelo
    Promise.all([
      this.comsTiendaCitaFuturaService.getAllTiendas('JA'),
      this.periodosService.getAllPeriodos('JA').toPromise(),
      this.periodosService.getPeriodosAbiertos('JA').toPromise()
    ]).then(([tiendas, periodosResponse, periodosAbiertosResponse]) => {
      // Procesar los datos
      this.todasLasTiendas = tiendas;
      this.todosLosPeriodos = periodosResponse;
      this.periodosAbiertos = periodosAbiertosResponse?.data;
      
      // Procesar datos de tiendas
      this.procesarDatosTiendas();
      
      // Configurar periodo inicial
      if (this.periodosAbiertos && this.periodosAbiertos.length > 0) {
        const periodoActual = this.periodosAbiertos[0];
        this.citaForm.patchValue({
          periodo: periodoActual.periodo
        });
        this.periodoSeleccionado = periodoActual.periodo;
      }
      
      // Cargar registros iniciales
      return this.cargarRegistrosCitaFutura();
    }).catch(error => {
      this.notificationService.errorMessage('Error al cargar los datos');
    }).finally(() => {
      this.notificationService.successMessage('Datos cargados correctamente');
      hideSpinner();
    });
  }

  ngAfterViewInit(): void {
    // Configurar dataSource
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  cargarRegistrosCitaFutura(): Promise<void> {
    this.isLoading = true;
    showSpinner();

    return this.comsTiendaCitaFuturaService.getPaginated('JA',
      this.currentPage + 1,
      this.pageSize,
      this.periodoSeleccionado
    ).pipe(
      finalize(() => {
        hideSpinner();
        this.isLoading = false;
      })
    ).toPromise()
      .then(response => {
        if (response) {
          this.dataSource.data = response.items || [];
          this.totalItems = response.total || 0;
          this.sourceIsEmpty = (response.items || []).length === 0;
        } else {
          this.dataSource.data = [];
          this.totalItems = 0;
          this.sourceIsEmpty = true;
        }
      })
      .catch(error => {
        this.notificationService.errorMessage('Error al cargar los registros');
        this.sourceIsEmpty = true;
        this.dataSource.data = [];
        this.totalItems = 0;
      });
  }

  onPageChange(event: any): void {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.cargarRegistrosCitaFutura();
  }

  onPeriodoChange(event: any): void {
    this.periodoSeleccionado = event.value;
    this.currentPage = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.cargarRegistrosCitaFutura();
  }

  async cargarDatos(): Promise<void> {
    try {
      this.todasLasTiendas = await this.comsTiendaCitaFuturaService.getAllTiendas('JA');
      
       // Carga los períodos en paralelo para mejor rendimiento
      const [responseTodos, responseAbiertos] = await Promise.all([
        this.periodosService.getAllPeriodos('JA').toPromise(),
        this.periodosService.getPeriodosAbiertos('JA').toPromise()
      ]);
  
      this.todosLosPeriodos = responseTodos;
      this.periodosAbiertos = responseAbiertos?.data;
      
      // Extraer departamentos y ciudades únicos
      this.procesarDatosTiendas();

    } catch (error) {
      this.notificationService.errorMessage('Error al cargar los datos: ' + error);
    }
  }

  private procesarDatosTiendas(): void {
    const departamentosMap = new Map<string, string>();
    const ciudadesMap = new Map<string, string>();
    
    this.todasLasTiendas.forEach(tienda => {
      if (tienda?.dep) {
        departamentosMap.set(tienda.dep, tienda.nombre_departamento || tienda.dep);
      }
      if (tienda?.ciu) {
        ciudadesMap.set(tienda.ciu, tienda.nombre_municipio || tienda.ciu);
      }
    });
  
    this.todosLosDepartamentos = Array.from(departamentosMap.entries())
      .map(([codigo, nombre]) => ({
        codigo,
        nombre_departamento: nombre
      }))
      .sort((a, b) => a.nombre_departamento.localeCompare(b.nombre_departamento));
  
    this.todasLasCiudades = Array.from(ciudadesMap.entries())
      .map(([codigo, nombre]) => ({
        codigo,
        nombre_municipio: nombre
      }))
      .sort((a, b) => a.nombre_municipio.localeCompare(b.nombre_municipio));
  
    this.tiendasDisponibles = [...this.todasLasTiendas];
  }

  getCiudadPlaceholder(): string {
    const count = this.ciudadesSeleccionadas.length;
    return count > 0 ? `${count} ciudad${count === 1 ? '' : 'es'} seleccionada${count === 1 ? '' : 's'}` : 'Buscar ciudad...';
  }

  getDepartamentoPlaceholder(): string {
    const count = this.departamentosSeleccionados.length;
    return count > 0 ? `${count} departamento${count === 1 ? '' : 's'} seleccionado${count === 1 ? '' : 's'}` : 'Buscar departamento...';
  }

  getTiendaPlaceholder(): string {
    const count = this.tiendasSeleccionadas.length;
    return count > 0 ? `${count} tienda${count === 1 ? '' : 's'} seleccionada${count === 1 ? '' : 's'}` : 'Buscar tienda...';
  }

  private _filterCiudades(value: string | null): {codigo: string, nombre_municipio: string}[] {
    // Primero filtramos las ciudades por los departamentos seleccionados
    let ciudadesFiltradas = [...this.todasLasCiudades];
    
    if (this.departamentosSeleccionados.length > 0) {
      // Obtenemos los códigos de departamento seleccionados
      const depCodigos = this.departamentosSeleccionados.map(d => d.codigo);
      
      // Filtramos las ciudades que pertenecen a los departamentos seleccionados
      ciudadesFiltradas = ciudadesFiltradas.filter(ciudad => {
        // Buscamos las tiendas que tienen esta ciudad
        const tiendasDeCiudad = this.todasLasTiendas.filter(t => t.ciu === ciudad.codigo);
        // Verificamos si alguna de estas tiendas pertenece a los departamentos seleccionados
        return tiendasDeCiudad.some(t => t.dep && depCodigos.includes(t.dep));
      });
    }

    // Luego aplicamos el filtro por texto si existe
    if (!value) return ciudadesFiltradas;
    const filterValue = typeof value === 'string' ? value.toLowerCase() : '';
    return ciudadesFiltradas.filter(ciudad => 
      ciudad.nombre_municipio.toLowerCase().includes(filterValue)
    );
  }

  private _filterDepartamentos(value: string | null): {codigo: string, nombre_departamento: string}[] {
    if (!value) return this.todosLosDepartamentos;
    const filterValue = typeof value === 'string' ? value.toLowerCase() : '';
    return this.todosLosDepartamentos.filter(departamento => 
      departamento.nombre_departamento.toLowerCase().includes(filterValue)
    );
  }

  private _filterTiendas(value: string | null): Tienda[] {
    if (!value) return this.tiendasDisponibles;
    const filterValue = typeof value === 'string' ? value.toLowerCase() : '';
    return this.tiendasDisponibles.filter(tienda => 
      tienda.nom.toLowerCase().includes(filterValue) ||
      tienda.c_age.toLowerCase().includes(filterValue)
    );
  }

  actualizarTiendasDisponibles(): void {
    let tiendasFiltradas = [...this.todasLasTiendas];

    // Filtrar por departamentos seleccionados
    if (this.departamentosSeleccionados.length > 0) {
      const depCodigos = this.departamentosSeleccionados.map(d => d.codigo);
      tiendasFiltradas = tiendasFiltradas.filter(t => t.dep && depCodigos.includes(t.dep));
    }

    // Filtrar por ciudades seleccionadas
    if (this.ciudadesSeleccionadas.length > 0) {
      const ciuCodigos = this.ciudadesSeleccionadas.map(c => c.codigo);
      tiendasFiltradas = tiendasFiltradas.filter(t => t.ciu && ciuCodigos.includes(t.ciu));
    }

    this.tiendasDisponibles = tiendasFiltradas;
    this.tiendaCtrl.updateValueAndValidity();
  }

  displayCiudad = (ciudad: {codigo: string, nombre_municipio: string}): string => {
    return ciudad ? ciudad.nombre_municipio : '';
  }

  displayDepartamento = (departamento: {codigo: string, nombre_departamento: string}): string => {
    return departamento ? departamento.nombre_departamento : '';
  }

  mostrarListaCiudades(): void {
    if (this.ciudadCtrl) {
      this.ciudadCtrl.setValue('');
    }
  }

  mostrarListaDepartamentos(): void {
    if (this.departamentoCtrl) {
      this.departamentoCtrl.setValue('');
    }
  }

  mostrarListaTiendas(): void {
    if (this.tiendaCtrl) {
      this.tiendaCtrl.setValue('');
    }
  }

  isCiudadSeleccionada(ciudad: {codigo: string, nombre_municipio: string}): boolean {
    return this.ciudadesSeleccionadas.some(c => c.codigo === ciudad.codigo);
  }

  isDepartamentoSeleccionado(departamento: {codigo: string, nombre_departamento: string}): boolean {
    return this.departamentosSeleccionados.some(d => d.codigo === departamento.codigo);
  }

  isTiendaSeleccionada(tienda: Tienda): boolean {
    return this.tiendasSeleccionadas.some(t => t.c_age === tienda.c_age);
  }

  toggleSeleccionCiudad(ciudad: {codigo: string, nombre_municipio: string}, event: MatCheckboxChange): void {
    if (event.checked) {
      this.ciudadesSeleccionadas.push(ciudad);
    } else {
      this.ciudadesSeleccionadas = this.ciudadesSeleccionadas.filter(c => c.codigo !== ciudad.codigo);
    }
    this.actualizarTiendasDisponibles();
  }

  toggleSeleccionDepartamento(departamento: {codigo: string, nombre_departamento: string}, event: MatCheckboxChange): void {
    if (event.checked) {
      this.departamentosSeleccionados.push(departamento);
    } else {
      this.departamentosSeleccionados = this.departamentosSeleccionados.filter(d => d.codigo !== departamento.codigo);
    }
    this.actualizarTiendasDisponibles();
  }

  toggleSeleccionTienda(tienda: Tienda, event: MatCheckboxChange): void {
    if (event.checked) {
      this.tiendasSeleccionadas.push(tienda);
    } else {
      this.tiendasSeleccionadas = this.tiendasSeleccionadas.filter(t => t.c_age !== tienda.c_age);
    }
  }

  get todasLasTiendasSeleccionadas(): boolean {
    return this.tiendasDisponibles.length > 0 && 
           this.tiendasSeleccionadas.length === this.tiendasDisponibles.length;
  }

  get algunasTiendasSeleccionadas(): boolean {
    return this.tiendasSeleccionadas.length > 0 && 
           this.tiendasSeleccionadas.length < this.tiendasDisponibles.length;
  }

  toggleTodasLasTiendas(event: MatCheckboxChange): void {
    if (event.checked) {
      this.tiendasSeleccionadas = [...this.tiendasDisponibles];
    } else {
      this.tiendasSeleccionadas = [];
    }
  }

  getNombreTienda(codigoAgencia: string): string {
    const tienda = this.todasLasTiendas.find(t => t.c_age === codigoAgencia);
    return tienda?.nom || 'N/A';
  }

  onSubmit(): void {
    if (this.citaForm.valid && this.tiendasSeleccionadas.length > 0) {
      const currentUser = getCurrentUser();
      if (!currentUser || !currentUser.usuario_aplicativo || !currentUser.c_emp) {
        this.notificationService.errorMessage('Error', 'No se pudo obtener la información del usuario actual');
        return;
      }

      const registrosAGuardar = this.tiendasSeleccionadas.map(tienda => ({
        c_emp: currentUser.c_emp!,
        c_age: String(tienda.c_age).trim(),
        periodo: String(this.citaForm.get('periodo')?.value).trim(),
        usuario_creacion: currentUser.usuario_aplicativo!,
        estado: 'ACTIVO'
      }));

      if (this.isEditing && this.registroSeleccionado && this.registroSeleccionado.id) {
        // Actualizar registro existente
        const registroActualizado = {
          ...registrosAGuardar[0],
          usuario_modificacion: currentUser.usuario_aplicativo,
          estado: 'ACTIVO'
        };

        showSpinner();
        this.comsTiendaCitaFuturaService.update('JA',this.registroSeleccionado.id, registroActualizado)
          .pipe(finalize(() => hideSpinner()))
          .subscribe({
            next: () => {
              this.notificationService.successMessage('Registro actualizado exitosamente');
              this.limpiarFormulario();
              this.cargarRegistrosCitaFutura();
            },
            error: (error) => {
              hideSpinner();
              this.notificationService.errorMessage('Error al actualizar el registro');
            }
          });
      } else {
        // Crear nuevos registros
        this.isLoading = true;
        showSpinner();
        let registrosCreados = 0;
        let huboError = false;
        let erroresDuplicados: string[] = [];
        
        // Crear un registro a la vez para evitar problemas de concurrencia
        const createRegistro = (index: number) => {
          if (index >= registrosAGuardar.length) {
            this.isLoading = false;
            hideSpinner();
            if (!huboError) {
              this.notificationService.successMessage(`${registrosCreados} registros guardados exitosamente`);
              this.limpiarFormulario();
              this.cargarRegistrosCitaFutura();
            } else if (erroresDuplicados.length > 0) {
              // Mostrar mensaje con las tiendas que ya existían
              const mensaje = erroresDuplicados.join('\n');
              this.notificationService.errorMessage(mensaje);
            }
            return;
          }

          const registroAGuardar = registrosAGuardar[index];

          this.comsTiendaCitaFuturaService.create('JA',registroAGuardar)
            .pipe(finalize(() => {
              if (index === registrosAGuardar.length - 1) {
                hideSpinner();
                this.isLoading = false;
              }
            }))
            .subscribe({
            next: (response) => {
              registrosCreados++;
              // Continuar con el siguiente registro
              createRegistro(index + 1);
            },
            error: (error) => {
              huboError = true;
              
              // Usar el mensaje de error del backend
              if (error.error?.detail) {
                erroresDuplicados.push(error.error.detail);
              } else {
                this.notificationService.errorMessage(
                  `Error al guardar el registro ${index + 1}. 
                  Se crearon ${registrosCreados} registros exitosamente.`
                );
              }
              
              // Continuar con el siguiente registro a pesar del error
              createRegistro(index + 1);
            }
          });
        };

        // Comenzar con el primer registro
        createRegistro(0);
      }
    }
  }

  eliminarRegistro(registro: ComsTiendaCitaFutura): void {
    if (!registro.id) {
      this.notificationService.errorMessage('No se puede eliminar un registro sin ID');
      return;
    }

    const currentUser = getCurrentUser();
    if (!currentUser || !currentUser.usuario_aplicativo || !currentUser.c_emp) {
      this.notificationService.errorMessage('Error', 'No se pudo obtener la información del usuario actual');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: { message: '¿Está seguro de eliminar este registro?' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        showSpinner();
        this.comsTiendaCitaFuturaService.deleteTiendaCitaFutura('JA',registro.id!).subscribe({
          next: () => {
            hideSpinner();
            this.notificationService.successMessage('Registro eliminado exitosamente');
            this.cargarRegistrosCitaFutura();
          },
          error: (error) => {
            hideSpinner();
            this.notificationService.errorMessage('Error al eliminar el registro');
          }
        });
      }
    });
  }

  limpiarFormulario(): void {
    this.citaForm.reset();
    this.ciudadesSeleccionadas = [];
    this.departamentosSeleccionados = [];
    this.tiendasSeleccionadas = [];
    this.isEditing = false;
    this.registroSeleccionado = null;
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  aplicarFiltros(): void {
    this.periodoSeleccionado = this.periodoFiltro;
    this.currentPage = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.cargarRegistrosCitaFutura();
  }
}
